/**
 * UsersTable - every user for the admin Users tab: signup, last seen, plan,
 * and the four onboarding emails (sent / opened). Backed by GET /admin/users.
 *
 * "Last seen" is users.last_login_at, which updates whenever the site loads
 * the user's profile, i.e. each visit while signed in.
 * Opens come from a tracking image in each email, so they are approximate:
 * Apple Mail loads images on arrival, and image-blocking clients never do.
 */
import { useCallback, useEffect, useState } from 'react';
import Card from '../common/Card';
import Badge from '../common/Badge';
import LoadingSpinner from '../common/LoadingSpinner';
import { listUsers } from '../../services/adminService';

const PAGE = 50;
const EMAILS = [
  { type: 'welcome', label: 'Welcome', day: 0 },
  { type: 'feature_spotlight', label: 'Streaks', day: 2 },
  { type: 'premium_preview', label: 'Premium', day: 5 },
  { type: 'checkin', label: 'Check-in', day: 7 },
];

function ago(iso) {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function EmailDot({ e, label }) {
  // grey = not sent yet, blue = sent, green = opened
  const state = e.opened_at ? 'opened' : e.sent_at ? 'sent' : 'pending';
  const color = { opened: 'bg-green-500', sent: 'bg-accent-blue', pending: 'bg-bg-tertiary border border-border' }[state];
  const title = state === 'opened'
    ? `${label}: opened ${new Date(e.opened_at).toLocaleString()} (${e.opens}x)`
    : state === 'sent' ? `${label}: sent ${new Date(e.sent_at).toLocaleString()}, not opened` : `${label}: not sent yet`;
  return <span title={title} className={`inline-block w-3 h-3 rounded-full ${color}`} />;
}

export default function UsersTable() {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('created');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await listUsers({ q: search, sort, limit: PAGE, offset }));
    } catch (e) {
      setError(e.response?.status === 403 ? 'Admin access required' : 'Could not load users');
    } finally {
      setLoading(false);
    }
  }, [search, sort, offset]);

  useEffect(() => { load(); }, [load]);

  const users = data?.users || [];
  const total = data?.total || 0;

  return (
    <Card className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <h2 className="text-xl font-bold text-text-primary flex-1">
          Users {data && <span className="text-text-secondary text-base font-normal">({total})</span>}
        </h2>
        <form
          onSubmit={(e) => { e.preventDefault(); setOffset(0); setSearch(q); }}
          className="flex gap-2"
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or name"
            className="px-3 py-1.5 text-sm rounded-lg bg-bg-tertiary border border-border text-text-primary"
          />
          <button type="submit" className="px-3 py-1.5 text-sm rounded-lg bg-accent-blue/20 text-accent-blue">Search</button>
        </form>
        <select
          value={sort}
          onChange={(e) => { setOffset(0); setSort(e.target.value); }}
          className="px-3 py-1.5 text-sm rounded-lg bg-bg-tertiary border border-border text-text-primary"
        >
          <option value="created">Newest signups</option>
          <option value="last_seen">Last seen</option>
          <option value="email">Email A-Z</option>
        </select>
      </div>

      <p className="text-xs text-text-secondary mb-3">
        Emails: <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500 align-middle" /> opened ·{' '}
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-accent-blue align-middle" /> sent ·{' '}
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-bg-tertiary border border-border align-middle" /> not sent.
        Hover a dot for times. Opens are approximate (Apple Mail loads images automatically).
        {data && !data.opens_tracked && ' Open tracking is not set up yet.'}
      </p>

      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
      {loading ? (
        <div className="py-10 flex justify-center"><LoadingSpinner /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary border-b border-border">
                <th className="text-left py-2 px-3">Email</th>
                <th className="text-left py-2 px-3">Plan</th>
                <th className="text-left py-2 px-3">Signed up</th>
                <th className="text-left py-2 px-3">Last seen</th>
                <th className="text-left py-2 px-3">Onboarding emails</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.email} className="border-b border-border/50 hover:bg-bg-tertiary/50">
                  <td className="py-2 px-3">
                    <div className="text-text-primary">{u.email}</div>
                    {u.display_name && <div className="text-xs text-text-secondary">{u.display_name}</div>}
                  </td>
                  <td className="py-2 px-3">
                    <Badge variant={u.tier === 'free' ? 'info' : 'success'} size="sm">{u.tier}</Badge>
                  </td>
                  <td className="py-2 px-3 text-text-secondary font-mono text-xs whitespace-nowrap">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="py-2 px-3 text-text-secondary text-xs whitespace-nowrap"
                      title={u.last_login_at ? new Date(u.last_login_at).toLocaleString() : ''}>
                    {ago(u.last_login_at) || 'never'}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1.5">
                      {EMAILS.map(({ type, label }) => {
                        const e = (u.emails || []).find((x) => x.type === type) || {};
                        return <EmailDot key={type} e={e} label={label} />;
                      })}
                      {u.onboarding_opted_out && <span className="text-xs text-text-secondary ml-1">opted out</span>}
                    </div>
                  </td>
                </tr>
              ))}
              {!users.length && !error && (
                <tr><td colSpan={5} className="py-6 text-center text-text-secondary">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE && (
        <div className="flex items-center justify-end gap-3 mt-4 text-sm text-text-secondary">
          <span>{offset + 1}-{Math.min(offset + PAGE, total)} of {total}</span>
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}
                  className="px-3 py-1 rounded bg-bg-tertiary disabled:opacity-40">Prev</button>
          <button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}
                  className="px-3 py-1 rounded bg-bg-tertiary disabled:opacity-40">Next</button>
        </div>
      )}
    </Card>
  );
}
